/**
 * Guests and the door queue (#126, #127).
 *
 * A guest record belongs to the organisation, not a venue: a group's regular
 * is a regular at all of its bars. It is the bar's data, held for them; it
 * is never read across organisations. A queue party belongs to one venue and
 * one night, and points at a guest rather than copying their number, so a
 * deletion request has one place to act.
 *
 * What BarOS can see of a visit is limited, and the records say so: every
 * visit carries its `source`. A queue-fed record knows when somebody last
 * *queued*, which over-represents full Friday nights and never sees the
 * Tuesday regular who walks straight in. The screens label it accordingly.
 *
 * Three consents, kept apart (#126, "two consents, not one"):
 *   - the phone number is for the queue, and nothing else;
 *   - the email, if given, is for the bar to reply to the guest;
 *   - marketing consent is its own unticked box, with when and how it was
 *     given, and it is the only thing that permits promotions.
 */
import { normaliseTastePreferences } from "./aroma.js";
// ------------------------------------------------------------------ phones
/**
 * An Australian mobile in E.164 (`+614XXXXXXXX`), or null for anything that
 * is not one. Mobiles only: the queue texts, and a landline cannot receive
 * a text. Accepts what people actually type at a door — spaces, dashes,
 * brackets, a leading 0 or +61 or 61.
 */
export function normaliseMobile(raw) {
    const digits = raw.replace(/[\s\-().]/g, "");
    let national;
    if (/^\+614\d{8}$/.test(digits))
        national = digits.slice(3);
    else if (/^614\d{8}$/.test(digits))
        national = digits.slice(2);
    else if (/^04\d{8}$/.test(digits))
        national = digits.slice(1);
    else if (/^4\d{8}$/.test(digits))
        national = digits;
    else
        return null;
    return `+61${national}`;
}
/** `+61412345678` → `0412 345 678`, as the number would be read out. */
export function displayMobile(e164) {
    const n = `0${e164.slice(3)}`;
    return `${n.slice(0, 4)} ${n.slice(4, 7)} ${n.slice(7)}`;
}
/** `+61412345678` → `0412 ··· 678`: enough to tell two guests apart, not enough to dial. */
export function maskedMobile(e164) {
    const n = `0${e164.slice(3)}`;
    return `${n.slice(0, 4)} ··· ${n.slice(7)}`;
}
export const NO_PREFS = { aromas: [], styles: [] };
/** Keep only the vocabulary we match on, de-duplicated, three families at most. */
export function cleanPrefs(raw) {
    const r = (raw ?? {});
    const clean = normaliseTastePreferences({
        families: Array.isArray(r.aromas) ? r.aromas : [],
        styles: Array.isArray(r.styles) ? r.styles : [],
    });
    return { aromas: clean.families, styles: clean.styles };
}
export const GUEST_LIST_SCOPES = ["org", "venue"];
export function settledGuestListScope(value) {
    return value === "venue" ? "venue" : "org";
}
/** The venues a guest is known at: those recorded, and where they were last seen. */
export function guestVenues(g) {
    return [...new Set([...(g.venueIds ?? []), ...(g.lastSeenVenueId ? [g.lastSeenVenueId] : [])])];
}
/**
 * How long a guest record is kept after they were last seen: two years
 * (Rob, 2026-10-03). Long enough to recognise somebody who comes once a
 * year; then DynamoDB's TTL deletes it, visits and phone pointer with it.
 */
export const GUEST_RETENTION_DAYS = 730;
/** The TTL (epoch seconds) for a guest's rows, from when they were last seen. */
export function guestTtl(lastSeenAt) {
    return Math.floor(new Date(lastSeenAt).getTime() / 1000) + GUEST_RETENTION_DAYS * 86_400;
}
/** How "last seen" reads, honest about where it came from. */
export function lastSeenLabel(source) {
    if (source === "queue")
        return "Last queued";
    if (source === "square")
        return "Last paid";
    return "Last seen";
}
export const OPEN_STATUSES = ["waiting", "ready"];
/** How long a guest has, after "your table's ready", before the bar may give it away. */
export const HOLD_MINUTES = 15;
/** The moves each state allows. Anything else is refused, so two taps cannot race a party backwards. */
const MOVES = {
    waiting: ["ready", "seated", "cancelled", "no-show"],
    ready: ["seated", "cancelled", "no-show", "waiting"],
    seated: [],
    cancelled: ["waiting"],
    "no-show": ["waiting"],
};
export function canMove(from, to) {
    return MOVES[from].includes(to);
}
export const MAX_PARTY_SIZE = 40;
export const MAX_GUEST_NAME = 60;
export const MAX_PARTY_NOTE = 200;
/**
 * How long a guest-page link works: until the end of the night, generously.
 * The row behind it carries a TTL, so an old link stops by itself.
 */
export const LINK_HOURS = 12;
/** Closed parties stay on the screen's history for a week, then go. */
export const PARTY_TTL_DAYS = 7;
// ------------------------------------------------------------------- texts
/**
 * The two texts, both service messages (#126, the Spam Act): about the
 * guest's own place in the queue, with nothing promotional in them. The
 * first carries the guest page's link as "tonight's drinks", which is the
 * menu — the offer that makes the link worth clicking — not a promotion.
 *
 * The sender is a shared "BarOS" ID for now, so the venue's name leads the
 * message: a text has to say who it is from.
 */
export function joinedText(venueName, name, link) {
    const base = `${venueName}: you're on the list, ${firstName(name)}. We'll text when your table's ready.`;
    // No link when the organisation does not have the guest page (D14).
    return link ? `${base} Tonight's drinks while you wait: ${link}` : base;
}
export function readyText(venueName, name) {
    return `${venueName}: your table's ready, ${firstName(name)}. Please come to the door within ${HOLD_MINUTES} minutes.`;
}
function firstName(name) {
    return name.trim().split(/\s+/)[0] ?? name;
}
export const GUEST_MENU_FIELDS = ["name", "style", "price"];
/**
 * Every field of a spec, decided: may it ever reach the guest page? Typed
 * as a record over `keyof Spec`, so adding a field to `Spec` does not
 * compile until somebody has decided here which side it falls on.
 */
export const SPEC_FIELD_PUBLICITY = {
    specId: "private",
    name: "public",
    status: "private",
    style: "public",
    tags: "private",
    method: "private",
    glass: "private",
    ice: "private",
    garnish: "private",
    batch: "private",
    creator: "private",
    added: "private",
    history: "private",
    notes: "private",
    sundries: "private",
    sheetGp: "private",
    talk: "private",
    lines: "private",
    variantOf: "private",
    version: "private",
    updatedAt: "private",
    updatedBy: "private",
    source: "private",
    sourceText: "private",
    reviewed: "private",
};
/**
 * Tonight's drinks at a venue, for the guest page: specs the bar has marked
 * available, priced at this venue, and not 86'd tonight.
 */
export function guestMenu(specs, overrides, showPrices) {
    const byId = new Map(overrides.map((o) => [o.specId, o]));
    return specs
        .filter((s) => s.status === "available")
        .flatMap((s) => {
        const o = byId.get(s.specId);
        if (!o || o.available === false || o.price === null || o.price === undefined)
            return [];
        return [{ name: s.name, style: [...s.style], price: showPrices ? o.price : null }];
    })
        .sort((a, b) => a.name.localeCompare(b.name));
}
// --------------------------------------------------------------- texting
/**
 * Texts an organisation may send in a month before the queue stops texting
 * and says so. The first feature whose cost scales with the bar being busy
 * (#126): two texts a party, a few cents each. Generous for one venue's
 * Fridays; a cap, like the AI one, so a mistake cannot run up a bill.
 */
export const DEFAULT_SMS_MONTHLY_LIMIT = 600;
/** `2026-10` — the month a text is counted against, in UTC like the AI meter. */
export function smsMonth(at) {
    return at.slice(0, 7);
}
//# sourceMappingURL=guests.js.map